import WSAdvanceDownload from "/WebInterface/new-ui/modules/advanced-download/ws.js"; // This will do WS downloads
import XHRAdvanceDownload from "/WebInterface/new-ui/modules/advanced-download/xhr.js"; // This will do normal XHR downloads
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import store from "/WebInterface/new-ui/store/index.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";

class DownloadingQueue {
  constructor() {
    this.queue = [];
    this.fileDownloadingInProgress = false;
    this.currentDownloadingFileInstance = null;
    this.currentFile = null;
    this.completedFilesFromQueue = [];
  }

  setQueue = (newQueue) => {
    this.queue = newQueue;
    this.onQueueUpdate(); // Call the method when queue updates
  };

  onQueueUpdate = () => {
    const queue = [...this.queue];

    store.dispatch("setChunkProcessingStatus", {
      type: "isFilesOnDownloadQueue",
      status: queue.length !== 0,
    });
  };

  addFileToQueue = (file) => {
    let queue = [...this.queue];
    queue.push(file);
    this.setQueue(queue);
    // Gets uploading status from store
    const { isUploading } = store.chunkProcessingStatus;

    if (isUploading) {
      _$("#downloadBlockedInfo").removeClass("is-hidden");
    }

    // Start auto download if uploading is not in progress
    if (queue.length === 1 && !isUploading) {
      this.startDownloadingCurrentFile(file);
    }

    store.dispatch("enableGlobalDownload");
  };

  remove = (fileId, removeFromStore = true) => {
    if (!fileId) return;
    let queue = [...this.queue];
    queue = queue.filter(({ id }) => id !== fileId);
    this.setQueue(queue);

    if (removeFromStore) {
      store.dispatch("deleteFileInDownload", { id: fileId });
    }
  };

  nextFileInDownloadingQueue = (canceled) => {
    // removes completed file
    let queue = [...this.queue];
    const done_file = queue.shift();
    this.completedFilesFromQueue.push(done_file);
    this.setQueue(queue);

    // start next file if available
    if (queue.length !== 0) {
      const file = queue[0];
      this.startDownloadingCurrentFile(file);
      return;
    }

    // Sets downloading status globally in the store :- false
    this.handleUpdateDownloadingStatus(false);

    if (canceled) {
      utils.titleAlert(`** ${t("downloadingCanceled")} **`, 1000);
    } else {
      utils.titleAlert(`** ${t("downloadCompleted")} **`, 1000);
    }

    // Passes status to upload iframe
    const iframe = document.querySelector("#fileUploadIframe");
    iframe.contentWindow.postMessage({
      key: "continueUpload",
    });

    this.completedFilesFromQueue = [];
    store.dispatch("disableGlobalDownload");
    store.dispatch("downloadingQueueFinished");
  };

  continueDownload = () => {
    const queue = [...this.queue];
    _$("#downloadBlockedInfo").addClass("is-hidden");

    if (queue.length === 0) return;
    const file = queue[0];

    this.startDownloadingCurrentFile(file);
  };

  // Update downloading status globally in the project
  handleUpdateDownloadingStatus = (downloading) => {
    if (
      (downloading === true || downloading === false) &&
      downloading !== this.fileDownloadingInProgress
    ) {
      this.fileDownloadingInProgress = downloading;

      store.dispatch("setChunkProcessingStatus", {
        type: "isDownloading",
        status: this.fileDownloadingInProgress,
      });
    }

    return this.fileDownloadingInProgress;
  };

  getDownloadTypeCustomizations = () => {
    const altDomains = utils.getAlternateDomains();

    const webSocketMaxRequestsDownload =
      store.userInfo.userCustomizations?.webSocketMaxRequestsDownload;

    const parseWebSocketRequest = () => {
      let requests = parseInt(webSocketMaxRequestsDownload);
      return isNaN(requests) || !requests ? 10 : requests;
    };

    return {
      parseWebSocketRequest,
      altDomains,
    };
  };

  startDownloadingCurrentFile = (file) => {
    this.currentFile = file;
    const { downloadType } = file;

    const { parseWebSocketRequest, altDomains } =
      this.getDownloadTypeCustomizations();

    this.currentDownloadingFileInstance =
      downloadType === "WS"
        ? new WSAdvanceDownload({
            maxRequest: parseWebSocketRequest(),
          })
        : new XHRAdvanceDownload({ altDomains });

    this.currentDownloadingFileInstance.startFileDownloading(file);
    store.dispatch("scrollToCurrentDownloadFile", { file });
    store.dispatch("enableGlobalDownload");
  };

  pause = () => {
    this.currentDownloadingFileInstance.pause();
  };

  cancel = () => {
    store.dispatch("updateFileStatusDownload", {
      fileId: this.currentFile.id,
      updates: {
        status: "canceling",
      },
    });

    this.currentDownloadingFileInstance.cancel();
  };

  resume = () => {
    this.currentDownloadingFileInstance.resume();
  };

  getCurrentDownloadingFile = () => {
    return this.currentFile;
  };

  getGlobalDownloadedFiles = () => {
    return [...this.completedFilesFromQueue, ...this.queue];
  };

  getCompletedFiles = () => {
    return this.completedFilesFromQueue;
  };
}

const downloadingQueue = new DownloadingQueue();
export default downloadingQueue;
